import {Modal, Text} from '@mantine/core';
import {Dropzone, IMAGE_MIME_TYPE} from '@mantine/dropzone';
import {MdInsertPhoto} from 'react-icons/md';
import {
  COLOR_BACKGROUND_1,
  COLOR_BACKGROUND_2,
  COLOR_LINE_1,
} from '../../../../../../config/constants/colorCodes';
import {ERROR_MESSAGES} from '../../../../constants/errorMessages';
import {
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_MB,
} from '../../../../constants/inputLimits';
import {Z_INDEX} from '../../../../constants/zIndex';
import {ModalCancelButton} from '../ModalCancelButton';
import {ModalSubmitButton} from '../ModalSubmitButton';

// アイコンサイズの定数
const ICON_SIZE = 64;

// Dropzoneのスタイル設定
const DROPZONE_STYLES = {
  root: {
    backgroundColor: COLOR_BACKGROUND_1,
    border: `2px dashed ${COLOR_LINE_1}`,
    borderRadius: '12px',
    padding: '24px',
    minHeight: '180px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: COLOR_BACKGROUND_2,
    },
  },
};

type ScreenshotUploadModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (file: File) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  isLoading?: boolean;
  error: string | null;
  onError: (message: string | null) => void;
};

export const ScreenshotUploadModal: React.FC<ScreenshotUploadModalProps> = ({
  opened,
  onClose,
  onSubmit,
  file,
  onFileChange,
  isLoading = false,
  error,
  onError,
}) => {
  const handleSubmit = () => {
    if (file) {
      onSubmit(file);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      radius={16}
      centered
      size="md"
      title="画像からAI自動入力"
      zIndex={Z_INDEX.MODAL}
      styles={{
        title: {
          fontWeight: 'bold',
        },
      }}
    >
      <div className="flex flex-col gap-24">
        <div className="flex flex-col gap-16">
          <Dropzone
            onDrop={(files) => {
              onError(null);
              onFileChange(files[0] || null);
            }}
            onReject={() => {
              onError(ERROR_MESSAGES.FILE_SIZE_LIMIT_EXCEEDED);
            }}
            accept={IMAGE_MIME_TYPE}
            maxFiles={1}
            maxSize={MAX_UPLOAD_FILE_SIZE_BYTES}
            styles={DROPZONE_STYLES}
          >
            <div className="flex flex-col items-center justify-center gap-8">
              <MdInsertPhoto size={ICON_SIZE} className="text-black-2" />
              <Text size="md" className="text-black-1 font-bold text-center">
                画像ファイルを選択またはドラッグ&ドロップ
              </Text>
              <Text size="xs" className="text-black-2">
                ファイル上限サイズ: {MAX_UPLOAD_FILE_SIZE_MB}MB
              </Text>
            </div>
          </Dropzone>
          {error && (
            <Text className="text-notification-1 text-center" role="alert">
              {error}
            </Text>
          )}
          {file && (
            <div className="flex flex-col gap-8 p-16 bg-background-1 rounded-12">
              <Text size="sm" className="font-bold">
                選択されたファイル
              </Text>
              <Text size="sm" className="text-black-1">
                {file.name}
              </Text>
              <Text size="xs" className="text-black-2">
                {(file.size / 1024).toFixed(2)} KB
              </Text>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-16">
          <ModalCancelButton onClick={onClose}>キャンセル</ModalCancelButton>
          <ModalSubmitButton
            onClick={handleSubmit}
            disabled={!file}
            isLoading={isLoading}
          >
            画像から抽出
          </ModalSubmitButton>
        </div>
      </div>
    </Modal>
  );
};
